/**
 * The partner register: applications first, then every partner.
 *
 * An application arrives from the Partner Portal's public form (or is added
 * here). Approving one gives its owner a first password they must change; an
 * approved partner earns nothing until an agreement is started on its page.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  Alert,
  Button,
  Can,
  Input,
  Modal,
  PageHeader,
  Select,
  ServerTable,
  createFacetsFetcher,
  createServerFetcher,
  formatDate,
  toast,
  type ServerColumn,
} from '@connect/web-shared';

import { PARTNER_STATUS, partnerKeys, partnersApi, type PartnerRow } from './api';
import { Chip, errorText } from './shared';

const ENDPOINT = '/partners';

export function PartnersPage() {
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const fetchRows = useMemo(() => createServerFetcher<PartnerRow>(ENDPOINT), []);
  const getFacets = useMemo(() => createFacetsFetcher(ENDPOINT), []);
  const alerts = useQuery({
    queryKey: partnerKeys.alerts,
    queryFn: () => partnersApi.alerts('open'),
  });

  const columns = useMemo<ServerColumn<PartnerRow>[]>(
    () => [
      {
        field: 'reference',
        header: 'Partner',
        filter: 'text',
        width: 150,
        render: (row) => <span className="font-mono text-xs text-muted">{row.reference}</span>,
      },
      { field: 'name', header: 'Name', filter: 'text', minWidth: 200 },
      {
        field: 'kind',
        header: 'Type',
        filter: 'select',
        width: 120,
        options: [
          { value: 'agency', label: 'Agency' },
          { value: 'reseller', label: 'Reseller' },
          { value: 'individual', label: 'Individual' },
        ],
      },
      {
        field: 'status',
        header: 'Status',
        filter: 'select',
        width: 130,
        render: (row) => <Chip value={row.status} map={PARTNER_STATUS} />,
      },
      { field: 'tier', header: 'Tier', filter: 'select', width: 110 },
      {
        field: 'active_customers',
        header: 'Customers',
        sortable: false,
        numeric: true,
        width: 110,
      },
      { field: 'city', header: 'City', filter: 'text', width: 130 },
      {
        field: 'applied_at',
        header: 'Applied',
        filter: 'date',
        width: 130,
        format: (v) => formatDate(v as string | null),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-1 flex-col gap-4">
      <PageHeader
        title="Partners"
        description="Agencies, resellers and individuals who bring customers, and their applications."
        actions={
          <Can permission="partners.partner.create">
            <Button onClick={() => setAdding(true)}>
              <Plus className="size-4" />
              Add a partner
            </Button>
          </Can>
        }
      />
      {alerts.data && alerts.data.length > 0 ? (
        <Alert tone="warning" title={`${alerts.data.length} open fraud alert${alerts.data.length === 1 ? '' : 's'}`}>
          Review them under Commissions → Alerts.
        </Alert>
      ) : null}
      <ServerTable<PartnerRow>
        columns={columns}
        fetchRows={fetchRows}
        getFacets={getFacets}
        rowKey={(row) => row.public_id}
        queryKey={partnerKeys.list}
        stateKey="partners.register"
        searchPlaceholder="Search by name, reference or city"
        onView={(row) => navigate(`/partners/${row.public_id}`)}
        emptyTitle="No partners yet"
        emptyDescription="Applications from the Partner Portal appear here, or add a partner yourself."
      />
      <AddPartner open={adding} onClose={() => setAdding(false)} onAdded={(id) => navigate(`/partners/${id}`)} />
    </div>
  );
}

function AddPartner({
  open,
  onClose,
  onAdded,
}: {
  open: boolean;
  onClose: () => void;
  onAdded: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: '', kind: 'agency', contact_name: '', email: '', phone: '', city: '' });
  const add = useMutation({
    mutationFn: () =>
      partnersApi.create({ ...form, phone: form.phone || null, city: form.city || null }),
    onSuccess: async (partner) => {
      toast.success(`${partner.name} added`, 'Approve it to give its owner a sign-in.');
      onClose();
      await queryClient.invalidateQueries({ queryKey: partnerKeys.all });
      onAdded(partner.public_id);
    },
  });
  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: event.target.value }));
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a partner"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={add.isPending}
            disabled={!form.name || !form.contact_name || !form.email}
            onClick={() => add.mutate()}
          >
            Add
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {add.isError ? <Alert tone="danger">{errorText(add.error)}</Alert> : null}
        <Input label="Name" value={form.name} onChange={set('name')} />
        <Select
          label="Type"
          value={form.kind}
          onChange={set('kind')}
          options={[
            { value: 'agency', label: 'Agency' },
            { value: 'reseller', label: 'Reseller' },
            { value: 'individual', label: 'Individual' },
          ]}
        />
        <Input label="Contact person" value={form.contact_name} onChange={set('contact_name')} />
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Email" type="email" value={form.email} onChange={set('email')} />
          <Input label="Phone" value={form.phone} onChange={set('phone')} />
        </div>
        <Input label="City" value={form.city} onChange={set('city')} />
      </div>
    </Modal>
  );
}
